import React, { useEffect, useMemo, useRef } from 'react';
import * as echarts from 'echarts';

const RSSI_TICKS = [-90, -80, -70, -60, -50, -40, -30];
const BAR_COLOR = '#4B68CF';

// Each rssi label (e.g. -50) represents -41 ~ -50 dBm; bars span 10 axis units with no gap.
const getRssiDrawRange = rssi => [rssi, rssi + 10];
const getRssiLabelRange = rssi => [rssi, rssi + 9];

export default function SignalQualityBarChart({ barChartData }) {
  const chartRef = useRef(null);

  // Extracts the numeric signal strength from a number or string (e.g., "-60dBm" -> -60)
  const parseRssi = rssi => {
    if (typeof rssi === 'number') return rssi;
    if (typeof rssi === 'string') {
      const matchedValue = rssi.match(/-?\d+/);
      return matchedValue ? Number(matchedValue[0]) : Number.NaN;
    }
    return Number.NaN;
  };

  const option = useMemo(() => {
    const safeData = Array.isArray(barChartData) ? barChartData : [];
    const sortedData = [...safeData].sort((a, b) => parseRssi(a?.rssi) - parseRssi(b?.rssi));
    const seriesData = sortedData.map(d => {
      const rssi = parseRssi(d?.rssi);
      const [rangeStart, rangeEnd] = getRssiDrawRange(rssi);
      const [, labelEnd] = getRssiLabelRange(rssi);
      return {
        value: [rangeStart, rangeEnd, d?.count ?? 0],
        labelEnd,
      };
    });
    const maxCount = Math.max(...seriesData.map(item => item.value[2]), 0);
    const isAllZero = maxCount === 0;

    const renderItem = (params, api) => {
      const rangeStart = api.value(0);
      const rangeEnd = api.value(1);
      const count = api.value(2);

      if (!count) return null;

      const bottomLeft = api.coord([rangeStart, 0]);
      const bottomRight = api.coord([rangeEnd, 0]);
      const topLeft = api.coord([rangeStart, count]);
      const barLeft = Math.min(bottomLeft[0], bottomRight[0]);
      const barWidth = Math.abs(bottomRight[0] - bottomLeft[0]);

      const rectShape = echarts.graphic.clipRectByRect(
        {
          x: barLeft,
          y: topLeft[1],
          width: barWidth,
          height: bottomLeft[1] - topLeft[1],
        },
        {
          x: params.coordSys.x,
          y: params.coordSys.y,
          width: params.coordSys.width,
          height: params.coordSys.height,
        },
      );

      if (!rectShape) return null;

      return {
        type: 'rect',
        shape: {
          ...rectShape,
          y: rectShape.y + rectShape.height,
          height: 0,
        },
        keyframeAnimation: {
          duration: 800,
          easing: 'cubicOut',
          keyframes: [
            {
              percent: 1,
              shape: rectShape,
            },
          ],
        },
        style: api.style({ fill: BAR_COLOR }),
      };
    };

    return {
      grid: {
        left: 56,
        right: 20,
        top: 26,
        bottom: 46,
        show: true,
        borderColor: '#C7C7CC',
        borderWidth: 1,
      },

      xAxis: {
        type: 'value',
        min: -90,
        max: -20,
        inverse: true,
        interval: 10,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: {
          show: true,
          lineStyle: {
            type: 'solid',
            color: '#C7C7CC',
            width: 1,
          },
        },
        axisLabel: {
          color: '#8E8E93',
          fontSize: 12,
          margin: 12,
          formatter: value => (RSSI_TICKS.includes(value) ? `${value} dBm` : ''),
        },
      },

      yAxis: {
        type: 'value',
        min: 0,
        max: isAllZero ? 50 : undefined,
        interval: isAllZero ? 10 : undefined,
        minInterval: 1,
        boundaryGap: ['0%', '30%'],
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: '#8E8E93', margin: 14 },
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            color: '#dcdfe6',
          },
        },
      },

      series: [
        {
          type: 'custom',
          renderItem,
          encode: {
            x: [0, 1],
            y: 2,
          },
          data: seriesData,
          itemStyle: { color: BAR_COLOR },
        },
      ],

      tooltip: {
        trigger: 'item',
        borderColor: '#C7C7CC',
        formatter: params => {
          const [rangeStart, , count] = params.data.value ?? params.data;
          const labelEnd = params.data.labelEnd ?? rangeStart + 9;
          const marker = `<span style="display:inline-block;margin-right:10px;border-radius:10px;width:10px;height:10px;background-color:${BAR_COLOR};"></span>`;
          return `${labelEnd} ~ ${rangeStart} dBm<br/>${marker}${count}`;
        },
      },
    };
  }, [barChartData]);

  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;

    const chart = echarts.init(el);
    chart.setOption(option);

    const resize = () => chart.resize();
    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
      chart.dispose();
    };
  }, [option]);

  return <div ref={chartRef} style={{ height: 260, width: '100%' }} />;
}
